import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Modal central reutilizado pelos formularios de criacao/edicao das
 * 3 entidades de configuracao. Sem dependencia externa (Radix Dialog
 * nao esta no projeto e nao queremos adicionar bibliotecas nesta rodada).
 *
 * Comportamento:
 *   - Overlay com backdrop blur (mesmo padrao do shell).
 *   - Foco inicial movido para o primeiro input ao abrir.
 *   - Escape fecha; click no overlay fecha.
 *   - Card com efeito glass usando var(--card-bg).
 */
interface ConfigModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function ConfigModal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: ConfigModalProps) {
  const cardRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    // Move focus para o primeiro input ao abrir.
    const t = window.setTimeout(() => {
      const first = cardRef.current?.querySelector<HTMLElement>(
        'input,select,textarea,[role="combobox"]',
      );
      first?.focus();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="config-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={cardRef}
        className={cn(
          'relative z-[61] w-full max-w-md overflow-hidden rounded-lg border shadow-xl',
          'border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-5 py-4">
          <div>
            <h2
              id="config-modal-title"
              className="text-base font-semibold text-[var(--fg)]"
            >
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
